// The pricing model a property's figures are calculated on (Neil, 10 Oct 2026: "select the pricing
// model to use for the calculations for the property and a confirm ... a recalculate button ... keep
// track on versions, who changed it and what the pricing model was ... warn ... that the calcs were
// based on an older version or different pricing model"). Every model has a version (a new UUID on
// each change, 100-api common/pricing_models.py); the order keeps the one its figures use, and the
// history of every change with its prices.
import { useState } from "react";
import { AlertTriangle, Calculator, CheckCircle2, History, RefreshCw, Star } from "lucide-react";
import { useApi, type OwnedBuildingDetail } from "../api";

const when = (iso?: string | null) =>
  iso
    ? new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })
    : "";
// A version as people read it: v1, v2 … (else "an earlier version", for orders from before the numbers)
const ver = (n?: number | null) => (n ? `v${n}` : "an earlier version");
const REASONS: Record<string, string> = { ordered: "Ordered on", changed: "Changed to", updated: "Updated to the latest", rate: "Set the", confirmed: "Confirmed" };

export function PricingModelCard({ b, onChange }: { b: OwnedBuildingDetail; onChange: (next: OwnedBuildingDetail) => void }) {
  const api = useApi();
  const ps = b.pricingStatus;
  const [chosen, setChosen] = useState(ps?.applied.id ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rate, setRate] = useState(ps?.rates?.importPence != null ? String(ps.rates.importPence) : "");
  if (!ps) return null;
  // The model's own rate, shown as the placeholder (the energy block's price is the one in use)
  const modelRate = b.energy?.prices.importPence;

  /** Save this property's own electricity rate, or clear it (null: the model's rate again). */
  const saveRate = async (value: number | null) => {
    if (value != null && !(value >= 5 && value <= 100)) {
      setError("Enter the electricity rate in pence a kWh, between 5 and 100.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const next = await api.setPropertyRates(b.orderId, value);
      onChange(next);
      setRate(next.pricingStatus?.rates?.importPence != null ? String(next.pricingStatus.rates.importPence) : "");
    } catch (e) {
      setError(e instanceof Error ? e.message : "That didn't save. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const applied = ps.applied;
  const option = ps.options.find((o) => o.id === chosen);
  const switching = chosen !== applied.id;
  // Recalculate when another model is chosen, or this one has a newer version
  const canRecalc = Boolean(option) && (switching || ps.outdated) && b.status !== "failed";
  // Confirm the model in use as it is (Neil, 11 Oct 2026: every property's model needs confirming,
  // even the default): the same call, which records the confirmation when nothing changes
  const confirmed = Boolean(ps.confirmed);
  const canConfirm = !confirmed && !canRecalc && Boolean(option) && !ps.deleted && b.status !== "failed";

  const confirm = async () => {
    if (!option) return;
    setBusy(true);
    setError(null);
    try {
      onChange(await api.setPropertyPricing(b.orderId, option.id));
    } catch (e) {
      setError(e instanceof Error ? e.message : "That didn't save. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const recalc = async () => {
    if (!option) return;
    const what = switching ? `Recalculate this property on “${option.name}”?` : `Recalculate this property on the latest version of “${option.name}”?`;
    if (!window.confirm(`${what}\n\nEvery figure on this page changes to the new prices, and the Solar Opportunity Report will need regenerating.`)) return;
    setBusy(true);
    setError(null);
    try {
      const next = await api.setPropertyPricing(b.orderId, option.id);
      onChange(next);
      setChosen(next.pricingStatus?.applied.id ?? option.id);
    } catch (e) {
      setError(e instanceof Error ? e.message : "The recalculation didn't work. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="pricing-model" className={`card pmc${confirmed ? "" : " pmc-unconfirmed"}`} aria-labelledby="pmc-title">
      <h2 id="pmc-title" className="building-h2">
        <Calculator size={18} aria-hidden="true" /> Pricing model
      </h2>
      {/* Not confirmed yet: every figure (and the report) uses this model, so the customer confirms it */}
      {confirmed ? (
        <p className="pmc-confirmed">
          <CheckCircle2 size={15} aria-hidden="true" /> Confirmed{ps.confirmed?.by ? ` by ${ps.confirmed.by}` : ""}
          {ps.confirmed?.at ? ` on ${when(ps.confirmed.at)}` : ""}
        </p>
      ) : (
        <p className="pmc-confirm-ask">
          <span className="pulse-star" aria-hidden="true">
            <Star size={14} />
          </span>
          Every figure on this page and in the report uses this pricing model. Check it's the right one and confirm it, even if it's your default.
        </p>
      )}
      <p className="pmc-now">
        The figures use <b>{applied.name}</b> <span className="pmc-ver">{ver(applied.version)}</span>
        {applied.appliedAt && (
          <span className="muted">
            {" "}
            · applied {when(applied.appliedAt)}
            {applied.appliedBy ? ` by ${applied.appliedBy}` : ""}
          </span>
        )}
      </p>

      {/* The warnings: the model changed since, or it's gone */}
      {ps.outdated && (
        <p className="pmc-warn">
          <AlertTriangle size={16} aria-hidden="true" />{" "}
          {/* Same number (our standard prices change with ours, not by a number), or none: say it changed */}
          {ps.latestVersion && applied.version && ps.latestVersion !== applied.version
            ? `These figures use “${applied.name}” ${ver(applied.version)}, but it's now on ${ver(ps.latestVersion)}.`
            : `“${applied.name}” has been updated since these figures were worked out.`}{" "}
          You may want to recalculate.
        </p>
      )}
      {ps.deleted && (
        <p className="pmc-warn">
          <AlertTriangle size={16} aria-hidden="true" /> “{applied.name}” has been deleted. The figures still use its prices; choose another model to
          recalculate.
        </p>
      )}

      {/* Label, control and actions in three columns, so the rows line up (stacked on a phone) */}
      <div className="pmc-choose">
        <label htmlFor="pmc-select">Calculate on</label>
        <select id="pmc-select" value={chosen} onChange={(e) => setChosen(e.target.value)} disabled={busy}>
          {ps.deleted && <option value={applied.id}>{applied.name} (deleted)</option>}
          {ps.options.map((o) => (
            <option key={o.id} value={o.id}>
              {o.name}
              {o.version ? ` v${o.version}` : ""}
              {o.isDefault ? " (your default)" : ""}
            </option>
          ))}
        </select>
        <div className="pmc-actions">
        {canConfirm ? (
          <button type="button" className="btn btn-primary" onClick={confirm} disabled={busy}>
            <CheckCircle2 size={15} aria-hidden="true" /> {busy ? "Confirming…" : "Confirm this pricing model"}
          </button>
        ) : (
          <button type="button" className="btn btn-primary" onClick={recalc} disabled={!canRecalc || busy}>
            <RefreshCw size={15} className={busy ? "spin" : undefined} aria-hidden="true" /> {busy ? "Recalculating…" : "Confirm and recalculate"}
          </button>
        )}
        </div>
      </div>
      {/* This property's own electricity rate, from a bill: makes the figures exact */}
      <div className="pmc-choose pmc-rate">
        <label htmlFor="pmc-rate">Electricity rate for this property</label>
        <div className="pmc-field">
        <input
          id="pmc-rate"
          type="number"
          inputMode="decimal"
          step="0.1"
          min={5}
          max={100}
          placeholder={ps.rates ? "" : `${modelRate ?? ""} (your model's)`}
          value={rate}
          onChange={(e) => setRate(e.target.value)}
          disabled={busy || b.status === "failed"}
        />
        <span className="muted">p/kWh</span>
        </div>
        <div className="pmc-actions">
        <button type="button" className="btn btn-secondary" onClick={() => saveRate(Number(rate))} disabled={busy || !rate || Number(rate) === ps.rates?.importPence}>
          Save rate
        </button>
        {ps.rates && (
          <button type="button" className="btn btn-secondary" onClick={() => saveRate(null)} disabled={busy}>
            Use the model's rate
          </button>
        )}
        </div>
      </div>
      <p className="pmc-sum">
        {ps.rates
          ? `The figures use ${ps.rates.importPence}p/kWh for this property${ps.rates.by ? `, set by ${ps.rates.by}` : ""}${ps.rates.at ? ` on ${when(ps.rates.at)}` : ""}.`
          : "From a recent bill, if you have one: the savings and payback then use this property's real rate."}
      </p>
      {error && <p className="pmc-error">{error}</p>}

      {/* Every change: when, who, which model and version, and its prices in one line */}
      {ps.history.length > 0 && (
        <details className="mine-history-wrap">
          <summary>
            <History size={13} aria-hidden="true" /> Pricing history ({ps.history.length})
          </summary>
          <ol className="pmc-history">
            {ps.history.map((h, i) => (
              <li key={i}>
                <div>
                  <b>
                    {REASONS[h.reason] ?? "Applied"} {h.name}
                  </b>{" "}
                  <span className="pmc-ver">{ver(h.version)}</span>
                </div>
                <div className="muted">
                  {when(h.at)}
                  {h.by ? ` · ${h.by}` : ""}
                </div>
                <div className="pmc-sum">{h.summary}</div>
              </li>
            ))}
          </ol>
        </details>
      )}
    </section>
  );
}
